import React, { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import {
  AlertBox,
  Headline,
  Icons,
  Spinner,
  Button,
  Field
} from '../../../index'
import { useCustomer, useConfig } from 'msp-integrations'
import { getAccountDataSharingConfig } from './AccountDataSharing.fields'
import { getStyles } from './AccountDataSharing.styles'
import { newsletterCheckboxLabel } from '@molecules/NewsletterForm/NewsletterForm.fields'

const AccountDataSharing = ({ customer }: { customer: any }) => {
  const { formatMessage } = useIntl()
  const { updateCustomer } = useCustomer()
  const {
    theme: {
      font,
      mspComponents: { accountDataSharing }
    }
  } = useConfig()
  const newsletterLabel = newsletterCheckboxLabel()
  const S = getStyles(font.regular, font.medium, accountDataSharing)
  const alertBoxRef = useRef<typeof AlertBox>()
  const [alertStyle, setAlertStyle] = useState('error')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [accountDataSharingConfig, setAccountDataSharingConfig] = useState<{
    configs: any
    newsletterLabel: any
  }>({ configs: null, newsletterLabel: null })
  const [hasValueChanges, setHasValueChanges] = useState(false)
  const {
    control,
    formState: { isSubmitting },
    watch,
    handleSubmit,
    setError,
    reset
  } = useForm()
  const watchAllFields = watch()

  useEffect(() => {
    if (customer?.customerId) {
      const { c_aboAlerts, c_decalcifyAlerts, c_newsletter } = customer
      const { aboAlerts, decalcifyAlerts, newsletter } = watchAllFields

      if (
        Boolean(aboAlerts) !== Boolean(c_aboAlerts) ||
        Boolean(decalcifyAlerts) !== Boolean(c_decalcifyAlerts) ||
        Boolean(newsletter) !== Boolean(c_newsletter)
      ) {
        setHasValueChanges(true)
      } else {
        setHasValueChanges(false)
      }
    }
  }, [watchAllFields])

  useEffect(() => {
    if (customer?.customerId) {
      const { c_aboAlerts, c_decalcifyAlerts, c_newsletter } = customer

      if (!accountDataSharingConfig.configs) {
        console.log(customer)
        const dataSharedValues = {
          configs: {
            c_aboAlerts: c_aboAlerts || false,
            c_decalcifyAlerts: c_decalcifyAlerts || false,
            c_newsletter: c_newsletter || false
          },
          newsletterLabel
        }

        setAccountDataSharingConfig(dataSharedValues)
      } else {
        reset({
          aboAlerts: c_aboAlerts,
          decalcifyAlerts: c_decalcifyAlerts,
          newsletter: c_newsletter
        })
      }
    }
  }, [customer])

  const updateAlerts = async (data: {
    aboAlerts: boolean
    decalcifyAlerts: boolean
    newsletter: boolean
  }) => {
    try {
      await updateCustomer({
        c_aboAlerts: !!data.aboAlerts,
        c_decalcifyAlerts: !!data.decalcifyAlerts,
        c_newsletter: !!data.newsletter,
        ...(!!data.newsletter && { c_newsletterSource: 'account' })
      })

      const genericSuccess = formatMessage({
        defaultMessage: 'Your data has been changed successfully.',
        id: 'msp.account_detail.datasharing.alert.success'
      })
      const newsletterSuccess = formatMessage({
        defaultMessage:
          'Newsletter Checkbox will be marked after accepting Double Optin Mail.',
        id: 'msp.account_detail.datasharing.alert.newsletter.success'
      })

      setAlertMessage(
        `${genericSuccess} ${data.newsletter ? newsletterSuccess : ''}`
      )
      setAlertStyle('success')
    } catch (e) {
      setAlertStyle('error')
      setError('global', { type: 'manual', message: '' })
      setAlertMessage(
        formatMessage({
          defaultMessage: 'Error saving your data. Please try again later!',
          id: 'msp.account_detail.datasharing.alert.error'
        })
      )
    }
    alertBoxRef?.current?.showAlert()
  }

  const config =
    (accountDataSharingConfig?.configs &&
      getAccountDataSharingConfig(
        accountDataSharingConfig?.configs,
        accountDataSharingConfig?.newsletterLabel
      )) ||
    false

  return (
    <div className='mt-2.5 lg:mt-0'>
      <AlertBox
        ref={alertBoxRef}
        type={alertStyle}
        position='right'
        title={alertMessage}
        autoCloseTime={4000}
        closeButtonIcon={<Icons iconName='Close' size='base' />}
      />
      <Headline tag='h6' className={S.title}>
        {formatMessage({
          defaultMessage: 'My data sharing',
          id: 'msp.account_detail.datasharing.title'
        })}
      </Headline>
      {config && (
        <form onSubmit={handleSubmit(updateAlerts)} id='data-sharing-form'>
          <div className='mb-14 flex flex-col'>
            <Headline tag='h3' className={S.subtitle}>
              {formatMessage({
                defaultMessage:
                  'Consent to receive marketing and advertising material',
                id: 'msp.account_detail.datasharing.newsletterTitle'
              })}
            </Headline>
            <p className={S.text}>
              {formatMessage({
                defaultMessage:
                  'You hereby agree to receive emails from Avoury about new products and services, promotions and gift cards. If you use the Avoury app, you can also manage your communication options in the app.',
                id: 'msp.account_detail.datasharing.newsletterText1'
              })}
            </p>
            <p className={S.text}>
              {formatMessage({
                defaultMessage:
                  'Regardless of your settings, you will continue to receive important messages such as changes to terms and conditions or software updates, as well as other routine customer support notifications (such as order information).',
                id: 'msp.account_detail.datasharing.newsletterText2'
              })}
            </p>
            <Field
              control={control}
              config={config.newsletter}
              isDisabled={isSubmitting}
              className={`${font.medium} leading-20`}
            />
          </div>
          <div className='mb-14 flex flex-col'>
            <Headline tag='h3' className={S.subtitle}>
              {formatMessage({
                defaultMessage: 'Alerts',
                id: 'msp.account_detail.datasharing.alertsTitle'
              })}
            </Headline>
            <p className={S.text}>
              {formatMessage({
                defaultMessage:
                  'We offer subscription reminders and descaling alerts. If you do not want to receive these emails, you can disable them here.',
                id: 'msp.account_detail.datasharing.alertsText'
              })}
            </p>
            <Field
              control={control}
              config={config.aboAlerts}
              isDisabled={isSubmitting}
              className={`${font.medium} leading-20`}
            />
            <Field
              control={control}
              config={config.decalcifyAlerts}
              isDisabled={isSubmitting}
              className={`${font.medium} leading-20`}
            />
          </div>
          <div className='mb-14 flex flex-col'>
            <Headline tag='h3' className={S.subtitle}>
              {formatMessage({
                defaultMessage: 'Use of the app and machine data.',
                id: 'msp.account_detail.datasharing.useOfTitle'
              })}
            </Headline>
            <p className={S.text}>
              {formatMessage({
                defaultMessage:
                  'Upon downloading the app and registering the machine, the usage data (number of cups of tea made, tea varieties, water consumption) of your machine will be transmitted via the app to your personal account and displayed on your dashboard and in your machine data in the app.',
                id: 'msp.account_detail.datasharing.useOfText'
              })}
            </p>
          </div>
          <Button
            className='lg:w-moz-fit block w-full lg:w-fit'
            isSubmit
            isDisabled={!hasValueChanges || isSubmitting}
            isLoading={isSubmitting}
            loadingContent={<Spinner />}
          >
            {formatMessage({
              defaultMessage: 'Save changes',
              id: 'msp.account_detail.datasharing.saveChangesBtn'
            })}
          </Button>
        </form>
      )}
    </div>
  )
}

export default AccountDataSharing
